import { useCallback, useEffect, useState } from "react";
import { NavBar } from "@/components/NavBar";
import { api, apiError, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Lock } from "lucide-react";

const FALLBACK = {
  cash: "https://images.unsplash.com/photo-1610375461246-83df859d849d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxnb2xkJTIwY29pbnN8ZW58MHx8fHwxNzg1OTYyNjk1fDA&ixlib=rb-4.1.0&q=85",
  vehicle: "https://images.pexels.com/photos/27639784/pexels-photo-27639784.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  default: "https://images.unsplash.com/photo-1567027757540-7b572280fa22?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxuZW9uJTIwY29udHJvbGxlcnxlbnwwfHx8fDE3ODU5NjI2OTV8MA&ixlib=rb-4.1.0&q=85",
};

export default function Rewards() {
  const { setUser } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [summary, setSummary] = useState(null);
  const [reds, setReds] = useState([]);

  const load = useCallback(async () => {
    const [r, s, d] = await Promise.all([
      api.get("/rewards"),
      api.get("/me/summary"),
      api.get("/me/redemptions"),
    ]);
    setRewards(r.data);
    setSummary(s.data);
    setUser(s.data.user);
    setReds(d.data);
  }, [setUser]);

  useEffect(() => {
    load();
  }, [load]);

  const redeem = async (id) => {
    try {
      const { data } = await api.post(`/rewards/${id}/redeem`);
      toast.success(`Redeemed. Type /redeem in-game to collect (code ${data.code}).`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const balance = summary?.user.points_balance ?? 0;
  const level = summary?.level.level ?? 1;

  return (
    <div className="min-h-screen">
      <NavBar />
      <main className="mx-auto max-w-7xl px-6 py-12" data-testid="rewards-page">
        <h1 className="text-4xl font-bold">Cash out your grind</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Balance <span className="mono text-primary">{fmt(balance)}</span> points · Level {level}
        </p>

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {rewards.map((r) => {
            const locked = level < r.min_level;
            const poor = balance < r.cost;
            return (
              <div key={r.id} className="card-hover flex flex-col border border-border bg-card" data-testid={`reward-card-${r.id}`}>
                <div className="relative h-40 overflow-hidden border-b border-border">
                  <img
                    src={r.image_url || FALLBACK[r.category] || FALLBACK.default}
                    alt={r.name}
                    className="h-full w-full object-cover opacity-80"
                  />
                  <Badge className="absolute left-3 top-3 rounded-none uppercase tracking-wider">{r.category}</Badge>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="text-lg font-semibold">{r.name}</h3>
                  <p className="mt-2 flex-1 text-sm text-muted-foreground">{r.description}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="mono text-xl text-primary">{fmt(r.cost)} pts</span>
                    {r.stock >= 0 && <span className="text-xs text-muted-foreground">{r.stock} left</span>}
                  </div>
                  <Button
                    className="mt-4 w-full rounded-none"
                    disabled={locked || poor || r.stock === 0}
                    onClick={() => redeem(r.id)}
                    data-testid={`redeem-btn-${r.id}`}
                  >
                    {locked ? (
                      <>
                        <Lock className="mr-2 h-3.5 w-3.5" /> Level {r.min_level} required
                      </>
                    ) : r.stock === 0 ? (
                      "Out of stock"
                    ) : poor ? (
                      `Need ${fmt(r.cost - balance)} more`
                    ) : (
                      "Redeem"
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <h2 className="mt-16 text-lg md:text-lg font-semibold">Your redemptions</h2>
        <div className="mt-4 border border-border bg-card" data-testid="my-redemptions">
          {reds.length === 0 && <p className="p-6 text-sm text-muted-foreground">Nothing redeemed yet.</p>}
          {reds.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-5 text-sm" data-testid={`redemption-${r.id}`}>
              <div>
                <div className="font-semibold">{r.reward_name}</div>
                <div className="text-xs text-muted-foreground">
                  {new Date(r.created_at).toLocaleString()} · code <span className="mono text-primary">{r.code}</span>
                  {r.status === "pending" && <> · type <span className="mono">/redeem</span> in-game to collect</>}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="mono text-muted-foreground">-{fmt(r.cost)}</span>
                <Badge
                  className="rounded-none uppercase"
                  variant={r.status === "fulfilled" ? "default" : r.status === "rejected" ? "destructive" : "secondary"}
                >
                  {r.status}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
